import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../../../../Components/UI/dialog';
import { Button } from '../../../../Components/UI/button';
import { Label } from '../../../../Components/UI/label';
import { Input } from '../../../../Components/UI/input';
import { Checkbox } from '../../../../Components/UI/checkbox';
import { ArrowLeft, X } from 'lucide-react';
import CustomSearchableDropdown from '../../../../Components/Common/CustomSearchableDD';
import { getUsersArr } from '../../../../Store/Reducers';
import { Check } from 'lucide-react';
import { getAuthData } from '../../../../Utils/AuthUtils';
// import { verifyMobile } from '../../APIs/yourAPIFile'; // Update this import path
import { toast } from 'sonner';
import { setGlobalLoading } from '../../../../Store/Actions';
import { sendOTPAPI, changeAdminAPI } from '../SystemChangeAPI';

const ChangeAdmin = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const users = useSelector(getUsersArr);
  const [selectedUserID, setSelectedUserID] = useState('');
  const [isOTPSend, setIsOTPSend] = useState(false);
  const [OTP, setOTP] = useState('');
  const [acceptConsequences, setAcceptConsequences] = useState(false);
  const [acceptTransactionImpact, setAcceptTransactionImpact] = useState(false);
  const [acceptTermsConditions, setAcceptTermsConditions] = useState(false);
  const authData = getAuthData();
  const currentUserID = authData?.UserID;
  const adminUserDetail = users.filter(user => user.userID === currentUserID)[0];
  const adminMobileDetail = {
    cc: adminUserDetail.cc,
    mobile: adminUserDetail.mobile
  };

  // Filter users except current admin
  const eligibleUsers = users?.filter(user => user.userID != currentUserID) || [];
  // Create options for dropdown
  const userOptions = eligibleUsers.map(user => ({
    value: user.userID,
    label: user.name || 'Unnamed User',
    subLabel: user.email || 'No email configured',
  }));

  const validateFields = () => {
    if (!adminMobileDetail.mobile || !adminMobileDetail.cc) {
      return "Admin mobile details are missing.";
    }
    // Validation
    if (!selectedUserID) {
      return 'Please select an executive.';
    }

    // Check if selected user has email configured
    const selectedUser = users.find(user => user.userID === selectedUserID);
    if (!selectedUser?.email || selectedUser.email.trim() === '') {
      return 'Email must be configured for admin. Please select a user with email configured.';
    }
    return null;
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedUserID('');
    setIsOTPSend(false);
    setOTP('');
    setAcceptConsequences(false);
    setAcceptTransactionImpact(false);
    setAcceptTermsConditions(false);
  };

  const handleOpenChange = (open) => {
    if (!open) {
      handleClose();
    }
  };

  const handleUserChange = (userID) => {
    setSelectedUserID(userID);
    // Reset verification state when user changes selection
    setIsOTPSend(false);
    setOTP('');
    setAcceptConsequences(false);
    setAcceptTransactionImpact(false);
    setAcceptTermsConditions(false);
  };

  // Handle mobile verification (Send OTP)
  const handleVerifyMobile = async () => {  
    const fromError = validateFields();
    if (fromError) {
      toast.error(fromError);
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      const response = await sendOTPAPI();
      if (response.error === false) {
        setIsOTPSend(true);
        toast.success(response.message);
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };


  const handleSubmit = async () => {
    // Validation
    if (!selectedUserID) {
      toast.error('Please select an executive.');
      return;
    }

    // Check if selected user has email configured
    const selectedUser = users.find(user => user.userID === selectedUserID);
    if (!selectedUser?.email || selectedUser.email.trim() === '') {
      toast.error('Email must be configured for admin. Please select a user with email configured.');
      return;
    }
    
    // Check if all checkboxes are selected
    if (!acceptConsequences || !acceptTransactionImpact || !acceptTermsConditions) {
      toast.error('Please accept all terms and conditions to proceed with admin change.');
      return;
    }

    if (!OTP || OTP.trim() === '') {
      toast.error('Please enter an OTP.');
      return;
    }

    if (OTP.length !== 4) {
      toast.error('OTP must be 4 digits.');
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      let data = {
        pocUserID: selectedUserID,
        cc: adminMobileDetail.cc,
        mobile: adminMobileDetail.mobile,
        otp: OTP,
      }
      const response = await changeAdminAPI('CHGADMIN', data);
      if (response.error === false) {
        toast.success('Admin changed.');
        handleClose();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to change admin. Please try again.');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input and max 4 characters
    if (/^\d{0,4}$/.test(value)) {
      setOTP(value);
    }
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUserID('');
      setIsOTPSend(false);
      setOTP('');
      setAcceptConsequences(false);
      setAcceptTransactionImpact(false);
      setAcceptTermsConditions(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="lg:!max-w-[400px]">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={handleClose}>
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Change Admin</div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Change Admin
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={handleClose}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Scrollable middle section */}
        <div className="lg:max-h-[70vh] max-h-[90vh] overflow-y-auto px-4">
          {/* User Selection Field - Full Width */}
          <div className="space-y-2">
            <Label htmlFor="adminUser" className="text-sm font-medium" required={true}>
              New Admin
            </Label>
            <CustomSearchableDropdown
              fieldID="adminUser"
              required={true}
              label=""
              placeholder="Select Executive"
              options={userOptions}
              value={selectedUserID}
              onChange={handleUserChange}
              emptyMessage={<>No users available.</>}
              onSelect={handleUserChange}
              searchEnabled={true}
              disabled={false}
            />
          </div>

          {/* Terms and Conditions Checkboxes - Show after OTP input */}
          {isOTPSend && (
            <div className="space-y-3 mt-6">
              <div className="mb-1 flex space-x-2">
                <Checkbox
                  id="acceptConsequences"
                  checked={acceptConsequences}
                  onCheckedChange={setAcceptConsequences}
                  className="mt-1"
                />
                <label htmlFor="acceptConsequences" className="cursor-pointer text-sm text-gray-700">
                  I accept the consequences of these changes.
                </label>
              </div>
              
              <div className="mb-1 flex space-x-2">
                <Checkbox
                  id="acceptTransactionImpact"
                  checked={acceptTransactionImpact}
                  onCheckedChange={setAcceptTransactionImpact}
                  className="mt-1"
                />
                <label htmlFor="acceptTransactionImpact" className="cursor-pointer text-sm text-gray-700">
                  I accept that the impact of some earlier transactions or interactions may not be updated even upon such changes.
                </label>
              </div>
              
              <div className="mb-1 flex space-x-2">
                <Checkbox
                  id="acceptTermsConditions"
                  checked={acceptTermsConditions}
                  onCheckedChange={setAcceptTermsConditions}
                  className="mt-1"
                />
                <label htmlFor="acceptTermsConditions" className="cursor-pointer text-sm text-gray-700">
                  I accept the <a href="#" className="text-sahi-primary-color hover:underline">Terms & Conditions</a> of SahiClub for such changes.
                </label>
              </div>
            </div>
          )}

          {/* OTP Input Field - Shows after mobile verification */}
          {isOTPSend && (
            <div className="space-y-2 mt-4">
              <Label htmlFor="OTP" className="text-sm font-medium" required={true}>
                OTP <p className="text-muted-foreground">(Sent by WhatsApp)</p>
              </Label>
              <Input
                id="OTP"
                type="password"
                placeholder=""
                value={OTP}
                onChange={handleOTPChange}
                maxLength={4}
                className="lg:w-[200px] w-full sahi-input"
              />
            </div>
          )}
        </div>
        
        {/* Footer with buttons */}
        <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-6 pt-0 border-t border-gray-200">
          {/* Send OTP Button - Shows only when user is selected and mobile not verified */}
          {!isOTPSend && (
            <Button
              onClick={handleVerifyMobile}
              className="w-full sm:w-auto"
            >
              <Check className="h-4 w-4"/>
              Send OTP
            </Button>
          )}
          
          {/* Change Admin Button - Shows only when mobile is verified */}
          {isOTPSend && (
            <Button
              onClick={handleSubmit}
              className="w-full sm:w-auto"
            >
              <Check className="h-4 w-4"/>
              Change Admin
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            <X className="w-3 h-3 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeAdmin;